import { Fragment, useState } from 'react';

import { BlurredImage } from 'components/Img';
import PlainButton from 'components/PlainButton';
import Rating, { useRatingScreenReaderTextGetter } from 'components/Rating';
import type { Review, ReviewImage } from 'models/api';
import { cn } from 'utils/classNames';
import { empty } from 'utils/helpers';

import type { ImageReviewItem } from '../hooks';
import { MediaDialog, MediaDialogZoomView } from '../ProductDetailsMedia';

interface BaseProps {
	id: string;
	images: ImageReviewItem[] | ReviewImage[] | undefined;
	isOpen: boolean;
	label: string;
	onClose: () => void;
	selectedImageIndex: number;
	setSelectedImageIndex: (index: number) => void;
	showThumbnails?: boolean;
}

interface PropsWithReviews extends BaseProps {
	images: ImageReviewItem[] | undefined;
	onReviewButtonClick: (review: Review) => void;
	readReviewButtonText: string;
}

interface PropsWithoutReviews extends BaseProps {
	images: ReviewImage[] | undefined;
	onReviewButtonClick?: never;
	readReviewButtonText?: never;
}

type Props = PropsWithReviews | PropsWithoutReviews;

export default function ReviewImagesDialog({
	id,
	images = empty.array,
	isOpen,
	label,
	onClose,
	onReviewButtonClick,
	readReviewButtonText,
	selectedImageIndex,
	setSelectedImageIndex,
	showThumbnails = true,
}: Props) {
	const getRatingScreenReaderText = useRatingScreenReaderTextGetter();
	const [isZoomed, setIsZoomed] = useState(false);

	return (
		<MediaDialog
			id={id}
			hasSingleImageFocus={isZoomed}
			imageCountTheme="dark"
			isOpen={isOpen}
			label={label}
			onClose={onClose}
			selectedImageIndex={selectedImageIndex}
			setSelectedImageIndex={setSelectedImageIndex}
			hasCustomThumbnailImageDimensions
			thumbnailImages={
				showThumbnails
					? images.map((image) => ({
							height: image.iconHeight,
							src: image.icon,
							width: image.iconWidth,
						}))
					: undefined
			}
			items={images.map((image) => (
				<Fragment key={image.icon}>
					<BlurredImage src={image.icon} className="absolute inset-0" />
					{'review' in image && (
						<PlainButton
							hidden={isZoomed}
							onClick={() => {
								onReviewButtonClick?.(image.review);
							}}
							aria-label={[
								image.review.author,
								getRatingScreenReaderText({
									reviewCountVariant: 'hidden',
									score: image.review.score,
								}),
								readReviewButtonText,
							].join(', ')}
							className={cn(
								'absolute bottom-r-md left-r-md z-2',
								'max-w-[calc(100%-6rem)] rounded-md px-3 py-2',
								'bg-positive/80 text-negative hover:bg-positive/90',
								'text-left text-sm',
								'group/review-button',
							)}
						>
							<span className="flex items-start gap-x-4">
								<span className="font-bold">{image.review.author}</span>
								<Rating
									score={image.review.score}
									size="small"
									reviewCountVariant="hidden"
									className="mt-0.5"
								/>
							</span>
							<span className="underline group-hover/review-button:underline-strong">
								{readReviewButtonText}
							</span>
						</PlainButton>
					)}
					<MediaDialogZoomView
						image={{
							src: image.large,
							width: image.largeWidth,
							height: image.largeHeight,
						}}
						isZoomed={isZoomed}
						onZoomStart={() => setIsZoomed(true)}
						onZoomStop={() => setIsZoomed(false)}
					/>
				</Fragment>
			))}
		/>
	);
}
ReviewImagesDialog.displayName = 'ReviewImagesDialog';
